/**
 * PreviewFormMount
 *
 * Drives the layout-editor form preview. Loads a read-only mock form into the
 * global `formStore` singleton (via `formStore.loadPreview`) whenever the
 * edited layout or preview visibility changes, and tears it down with
 * `formStore.exitPreview()` on unmount (which avoids clobbering a real form
 * the URL listener may have already navigated to). The preview then renders the real form
 * subtree (`FormUI`) against the singleton — the same source of truth regular
 * forms read — so no rendering component needs to know it is a preview.
 *
 * Wraps the subtree in `FormWorkflowActionProvider` because `ButtonUI` mounts
 * `FormWorkflowSelect`, which requires it. Buttons render disabled via
 * `forceDisabled`, so the workflow control is inert. `FormRequiredFieldsModal`
 * mounts alongside it so a gated action reached from the preview still has its
 * dialog — the provider no longer owns that modal.
 */

import { FC, RefObject, useContext, useEffect } from 'react';
import type { Form, FormType, FormVersion, VisibilityTypeId } from '#types/System.types';
import { LayoutEditorContext } from './LayoutEditorContext';
import { formStore } from '../../store';
import { FormWorkflowActionProvider } from '../artifacts/FormWorkflowActionProvider';
import FormUI from '../../components/rendering/FormUI';
import FormRequiredFieldsModal from '../../components/modals/FormRequiredFieldsModal';

interface PreviewFormMountProps {
    /** Content container ref forwarded to `FormUI` for external DOM manipulation. */
    contentRef: RefObject<HTMLDivElement | null>;
}

export const PreviewFormMount: FC<PreviewFormMountProps> = ({ contentRef }) => {
    const editor = useContext(LayoutEditorContext);
    const layout = editor?.layout;
    const visibilityMode = (editor?.previewVisibilityMode || 'internal') as VisibilityTypeId;

    useEffect(() => {
        if (!layout) return;
        formStore.loadPreview({
            form: {
                docId: 'preview',
                title: 'Preview Form',
                formType: 'preview',
                currentVersionId: 'preview',
                organization: 'Preview',
                accessGroups: {},
            } as Form,
            formType: {
                docId: 'preview',
                defaultUILayout: layout,
                possibleStates: { draft: { allowedNextStates: ['draft'] } },
            } as FormType,
            formVersion: {
                docId: 'preview',
                state: 'draft',
                fields: {},
                entityReferences: {},
            } as FormVersion,
            visibilityMode,
        });
        return () => {
            formStore.exitPreview();
        };
    }, [layout, visibilityMode]);

    if (!editor) {
        throw new Error('PreviewFormMount must be used within a LayoutEditorProvider');
    }

    return (
        <FormWorkflowActionProvider formId="preview">
            <FormUI forceDisabled contentRef={contentRef} />
            <FormRequiredFieldsModal />
        </FormWorkflowActionProvider>
    );
};
